import React, { useState, useEffect, useRef, useCallback } from 'react';
import ProtectedLayout from '../components/ProtectedLayout';
import api from '../api';
import { toast } from 'sonner';

// ============================================================
// UNDERWRITING SEARCH COMPONENT
// ============================================================
function UnderwritingSearch({ uwItems, setUwItems }) {
  const [searchTerm, setSearchTerm] = useState('');
  const [searchState, setSearchState] = useState(0); // 0=idle,1=searching,2=results
  const [results, setResults] = useState([]);
  const [lastTerm, setLastTerm] = useState('');
  const debounceRef = useRef(null);
  const inputRef = useRef(null);
  const resultsRef = useRef(null);
  const [focused, setFocused] = useState(false);

  const doSearch = async (term) => {
    if (term.length <= 2) { setSearchState(0); return; }
    setLastTerm(term);
    setSearchState(1);
    try {
      const res = await api.post('/quoter/uw-search', { search: term });
      if (term !== inputRef.current?.value) return; // stale
      setResults(res.data.results || []);
      setSearchState(2);
      if (resultsRef.current) resultsRef.current.scrollTop = 0;
    } catch (e) {
      setSearchState(0);
    }
  };

  const handleInput = (e) => {
    const val = e.target.value;
    setSearchTerm(val);
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => doSearch(val), 400);
  };

  const selectResult = async (result) => {
    // Add item with active_question = 1, then get first question
    const item = { ...result, active_question: 1, questions: [], completed: false };
    setSearchTerm('');
    setSearchState(0);
    setResults([]);

    // Get the first question
    try {
      const res = await api.post('/quoter/uw-next-question', { uw_item: item });
      if (res.data.next) {
        const q = { ...res.data.next, answer: [], currently: '', last_treated_type: 'exact', last_treated_month: '', last_treated_day: '', last_treated_year: '' };
        if (res.data.indications) item.indications = res.data.indications;
        item.questions = [q];
        item.active_question = 1;
      }
    } catch (e) { /* use item without questions */ }

    setUwItems(prev => [...prev, item]);
  };

  const cancel = () => { setSearchTerm(''); setSearchState(0); setResults([]); };

  return (
    <div>
      <div style={{ fontWeight: '600', marginBottom: '10px', fontSize: '14px' }}>
        Medications &amp; Personal/Medical History
      </div>
      <div style={{ position: 'relative', zIndex: 20 }}>
        <input
          ref={inputRef}
          type="search"
          className="input-field"
          placeholder="Search for medications, conditions, etc."
          value={searchTerm}
          onChange={handleInput}
          onFocus={() => setFocused(true)}
          onBlur={() => setTimeout(() => setFocused(false), 200)}
        />
        {searchState === 1 && (
          <div style={dropdownStyle}>
            <span style={{ color: '#64748B', fontSize: '13px' }}>Searching...</span>
          </div>
        )}
        {searchState === 2 && results.length === 0 && (
          <div style={dropdownStyle}>
            <span style={{ color: '#64748B', fontSize: '13px' }}>No results found for &ldquo;{lastTerm}&rdquo;.</span>
          </div>
        )}
        {searchState === 2 && results.length > 0 && (
          <div style={{ ...dropdownStyle, padding: 0 }} ref={resultsRef}>
            {results.map(r => (
              <button
                key={r.id}
                onMouseDown={() => selectResult(r)}
                style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '8px 12px', width: '100%', textAlign: 'left', background: 'none', border: 'none', cursor: 'pointer', borderBottom: '1px solid #F1F5F9', fontSize: '13px' }}
                onMouseEnter={e => e.currentTarget.style.background = '#F8FAFC'}
                onMouseLeave={e => e.currentTarget.style.background = 'none'}
              >
                <i className={r.type_id === 1 ? 'fas fa-prescription-bottle-medical' : 'fas fa-book-medical'} style={{ color: '#2D87F0', fontSize: '16px', width: '16px' }}></i>
                <div>
                  <div style={{ fontWeight: '600' }}>{r.title !== r.search_term ? r.search_term : r.title}</div>
                  {r.title !== r.search_term && <div style={{ color: '#64748B', fontSize: '12px' }}>({r.title})</div>}
                </div>
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Selected items */}
      <div style={{ display: 'flex', flexDirection: 'column', paddingTop: '8px', gap: '0' }}>
        {uwItems.map((item, i) => (
          <UwItemRow key={item.id + i} item={item} index={i} setUwItems={setUwItems} />
        ))}
      </div>
    </div>
  );
}

const dropdownStyle = {
  position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 20,
  background: 'white', border: '1px solid #DDE7F1', borderRadius: '6px',
  boxShadow: 'var(--shadow-dropdown)', maxHeight: '220px', overflowY: 'auto',
  marginTop: '2px', padding: '8px 12px',
};

// ============================================================
// SINGLE UW ITEM ROW (with expandable question flow)
// ============================================================
function UwItemRow({ item, index, setUwItems }) {
  const isExpanded = item.active_question > 0;
  const currentQ = item.questions?.[item.active_question - 1];

  const update = (updater) => {
    setUwItems(prev => {
      const next = [...prev];
      next[index] = updater(next[index]);
      return next;
    });
  };

  const toggleEdit = () => update(it => ({ ...it, active_question: it.active_question > 0 ? 0 : 1 }));
  const remove = () => setUwItems(prev => prev.filter((_, i) => i !== index));

  const handleNextQuestion = async () => {
    // Update current question answer
    let updatedItem = { ...item };
    if (currentQ) {
      const q = { ...currentQ };
      if (['drug_last_taken', 'condition_last_treated'].includes(q.type)) {
        if (q.currently === 'yes') q.answer = 'now';
        else if (q.last_treated_type !== 'exact') q.answer = 'approx:' + q.last_treated_type;
        else q.answer = `${q.last_treated_month || ''}/${q.last_treated_day || ''}/${q.last_treated_year || ''}`;
      }
      updatedItem.questions = [...(updatedItem.questions || [])];
      updatedItem.questions[item.active_question - 1] = q;

      // If drug_indication → next is drug_last_taken (already in questions usually)
      if (q.type === 'drug_indication') {
        // Collapse and mark done (next question will be fetched)
        try {
          const res = await api.post('/quoter/uw-next-question', { uw_item: updatedItem });
          if (res.data.next) {
            const nextQ = { ...res.data.next, answer: [], currently: '', last_treated_type: 'exact', last_treated_month: '', last_treated_day: '', last_treated_year: '' };
            updatedItem.questions = [...updatedItem.questions, nextQ];
            updatedItem.active_question = updatedItem.questions.length;
          } else {
            updatedItem.completed = true;
            updatedItem.active_question = 0;
          }
        } catch (e) {
          updatedItem.active_question = 0;
        }
        update(() => updatedItem);
        return;
      }
    }

    // Try to advance to next question
    if (updatedItem.active_question < (updatedItem.questions?.length || 0)) {
      updatedItem.active_question += 1;
      update(() => updatedItem);
      return;
    }

    // Fetch next question from backend
    try {
      const res = await api.post('/quoter/uw-next-question', { uw_item: updatedItem });
      if (res.data.next) {
        const nextQ = { ...res.data.next, answer: [], currently: '', last_treated_type: 'exact', last_treated_month: '', last_treated_day: '', last_treated_year: '' };
        if (res.data.indications) updatedItem.indications = res.data.indications;
        updatedItem.questions = [...(updatedItem.questions || []), nextQ];
        updatedItem.active_question = updatedItem.questions.length;
      } else {
        updatedItem.completed = true;
        updatedItem.active_question = 0;
      }
    } catch (e) {
      updatedItem.active_question = 0;
    }
    update(() => updatedItem);
  };

  const updateQ = (qIndex, changes) => {
    update(it => {
      const qs = [...(it.questions || [])];
      qs[qIndex] = { ...qs[qIndex], ...changes };
      return { ...it, questions: qs };
    });
  };

  const displayName = item.title !== item.search_term ? item.search_term : item.title;

  return (
    <div style={{ marginBottom: isExpanded ? '0' : '8px' }}>
      {/* Item header */}
      <div style={{
        border: '1px solid #DDE7F1', borderRadius: isExpanded ? '8px 8px 0 0' : '8px',
        padding: '8px 12px', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start',
        background: isExpanded ? 'rgba(45,135,240,0.08)' : 'white',
        position: 'relative', zIndex: 10, transition: 'background 150ms',
      }}>
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <i className={item.type_id === 1 ? 'fas fa-prescription-bottle-medical' : 'fas fa-book-medical'} style={{ color: '#2D87F0', fontSize: '16px' }}></i>
            <span style={{ fontWeight: '600', fontSize: '13px' }}>{displayName}</span>
            {item.completed && <i className="fas fa-check-circle" style={{ color: '#16A34A', fontSize: '13px' }}></i>}
          </div>
          {item.title !== item.search_term && (
            <div style={{ fontSize: '12px', color: '#64748B', paddingLeft: '24px' }}>({item.title})</div>
          )}
        </div>
        <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
          <button onClick={toggleEdit} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#64748B', fontSize: '13px', padding: '2px 6px' }}
            onMouseEnter={e => e.target.style.color = '#2D87F0'} onMouseLeave={e => e.target.style.color = '#64748B'}>
            <i className="fas fa-edit"></i>
          </button>
          <button onClick={remove} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#64748B', fontSize: '13px', padding: '2px 6px' }}
            onMouseEnter={e => e.target.style.color = '#DC2626'} onMouseLeave={e => e.target.style.color = '#64748B'}>
            <i className="fas fa-times"></i>
          </button>
        </div>
      </div>

      {/* Question panel */}
      {isExpanded && currentQ && (
        <div style={{
          border: '1px solid #DDE7F1', borderTop: 'none', borderRadius: '0 0 8px 8px',
          padding: '16px', background: 'white', display: 'flex', flexDirection: 'column', gap: '12px',
          marginBottom: '8px',
        }}>
          {/* drug_indication */}
          {currentQ.type === 'drug_indication' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              <div style={{ fontWeight: '600', fontSize: '13px' }}>
                What are you taking <em>{displayName}</em> for?
              </div>
              {(item.indications || []).map(ind => (
                <label key={ind.id} style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', fontSize: '13px' }}>
                  <input type="radio" name={`ind_${item.id}_${index}`} value={ind.id}
                    checked={currentQ.answer === ind.id}
                    onChange={() => updateQ(item.active_question - 1, { answer: ind.id })}
                  />
                  {ind.title}
                </label>
              ))}
              <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', fontSize: '13px' }}>
                <input type="radio" name={`ind_${item.id}_${index}`} value="other"
                  checked={currentQ.answer === 'other'}
                  onChange={() => updateQ(item.active_question - 1, { answer: 'other' })}
                />
                Other
              </label>
            </div>
          )}

          {/* drug_last_taken OR condition_last_treated */}
          {(currentQ.type === 'drug_last_taken' || currentQ.type === 'condition_last_treated') && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
              <div style={{ fontWeight: '600', fontSize: '13px' }}>
                {currentQ.type === 'drug_last_taken'
                  ? <>Are you currently taking <em>{displayName}</em>?</>
                  : <>Do you currently have <em>{displayName}</em>?</>}
              </div>
              <div style={{ display: 'inline-flex', borderRadius: '4px', overflow: 'hidden' }}>
                {['yes', 'no'].map(v => (
                  <label key={v} style={{ cursor: 'pointer' }}>
                    <input type="radio" value={v} checked={currentQ.currently === v}
                      onChange={() => updateQ(item.active_question - 1, { currently: v })}
                      style={{ display: 'none' }}
                    />
                    <div style={{
                      padding: '6px 16px', border: '1px solid #2D87F0',
                      borderLeft: v === 'no' ? 'none' : '1px solid #2D87F0',
                      background: currentQ.currently === v ? '#2D87F0' : 'white',
                      color: currentQ.currently === v ? 'white' : '#2D87F0',
                      fontSize: '13px', fontWeight: '500', cursor: 'pointer',
                      borderRadius: v === 'yes' ? '4px 0 0 4px' : '0 4px 4px 0',
                    }}>
                      {v === 'yes' ? 'Yes' : 'No'}
                    </div>
                  </label>
                ))}
              </div>

              {currentQ.currently === 'no' && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  <div style={{ fontWeight: '600', fontSize: '13px' }}>
                    {currentQ.type === 'drug_last_taken'
                      ? <>When did you last take <em>{displayName}</em>?</>
                      : <>When were you last treated for <em>{displayName}</em>?</>}
                  </div>
                  <select className="input-field" value={currentQ.last_treated_type}
                    onChange={e => updateQ(item.active_question - 1, { last_treated_type: e.target.value })}
                    style={{ appearance: 'none', cursor: 'pointer' }}>
                    <option value="exact">Exact Date</option>
                    <option value="0">Within the last 12 months</option>
                    {[1, 2, 3, 4, 5, 10].map(y => (
                      <option key={y} value={String(y)}>{y} year{y > 1 ? 's' : ''} ago</option>
                    ))}
                  </select>
                  {currentQ.last_treated_type === 'exact' && (
                    <div style={{ display: 'flex', gap: '8px' }}>
                      <input className="input-field" type="tel" placeholder="MM" maxLength={2} style={{ flex: 1 }}
                        value={currentQ.last_treated_month || ''}
                        onChange={e => updateQ(item.active_question - 1, { last_treated_month: e.target.value })} />
                      <input className="input-field" type="tel" placeholder="DD" maxLength={2} style={{ flex: 1 }}
                        value={currentQ.last_treated_day || ''}
                        onChange={e => updateQ(item.active_question - 1, { last_treated_day: e.target.value })} />
                      <input className="input-field" type="tel" placeholder="YYYY" maxLength={4} style={{ flex: 2 }}
                        value={currentQ.last_treated_year || ''}
                        onChange={e => updateQ(item.active_question - 1, { last_treated_year: e.target.value })} />
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
            <button onClick={handleNextQuestion} className="btn-primary" style={{ padding: '6px 18px', fontSize: '13px', height: '34px' }}>
              Next
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// ============================================================
// MEDICAL/PERSONAL HISTORY MODAL
// ============================================================
function UwHistoryModal({ visible, onClose, form, answers, setAnswers }) {
  if (!visible || !form) return null;

  const subsections = form?.details?.sections?.[1]?.subsections || [];

  const checkVisibility = (field) => {
    if (!field?.visibility_logic?.conditions?.length) return true;
    const conds = field.visibility_logic.conditions;
    const op = field.visibility_logic.operator || 'and';
    const check = (cond) => answers[cond.field_key] === cond.value;
    return op === 'or' ? conds.some(check) : conds.every(check);
  };

  const setAnswer = (key, value) => setAnswers(prev => ({ ...prev, [key]: value }));

  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 100,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      background: 'rgba(0,0,0,0.2)', backdropFilter: 'blur(2px)',
    }} onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div style={{
        background: 'white', borderRadius: '12px', border: '1px solid #DDE7F1',
        width: '100%', maxWidth: '540px', maxHeight: '90vh',
        display: 'flex', flexDirection: 'column', overflow: 'hidden',
        boxShadow: 'var(--shadow-dropdown)',
      }}>
        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '16px 20px', borderBottom: '1px solid #DDE7F1' }}>
          <h2 style={{ fontWeight: '700', fontSize: '16px', margin: 0 }}>Health &amp; Personal History</h2>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '18px', color: '#64748B', width: '20px', height: '20px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <i className="fas fa-times"></i>
          </button>
        </div>

        {/* Scrollable body */}
        <div style={{ overflowY: 'auto', flex: 1, padding: '16px 20px' }}>
          {subsections.map((sub) => (
            <div key={sub.key} style={{ marginBottom: '24px' }}>
              <div style={{ fontWeight: '700', fontSize: '13px', textTransform: 'uppercase', letterSpacing: '0.05em', color: '#64748B', marginBottom: '12px', paddingBottom: '6px', borderBottom: '1px solid #F1F5F9' }}>
                {sub.title}
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
                {sub.fields.map((field) => {
                  if (!checkVisibility(field)) return null;
                  return (
                    <div key={field.key} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '16px' }}>
                      <label style={{ fontWeight: '500', fontSize: '13px', color: '#0F172A', flex: 1 }}>{field.title}</label>

                      {field.type === 'yes_no' && (
                        <div style={{ display: 'inline-flex', borderRadius: '4px', overflow: 'hidden', flexShrink: 0 }}>
                          {['no', 'yes'].map(v => (
                            <label key={v} style={{ cursor: 'pointer' }}>
                              <input type="radio" name={field.key} value={v}
                                checked={answers[field.key] === v}
                                onChange={() => setAnswer(field.key, v)}
                                style={{ display: 'none' }}
                              />
                              <div style={{
                                padding: '5px 14px', fontSize: '13px', fontWeight: '500',
                                border: '1px solid #2D87F0',
                                borderLeft: v === 'yes' ? 'none' : '1px solid #2D87F0',
                                background: answers[field.key] === v ? '#2D87F0' : 'white',
                                color: answers[field.key] === v ? 'white' : '#2D87F0',
                                cursor: 'pointer',
                                borderRadius: v === 'no' ? '4px 0 0 4px' : '0 4px 4px 0',
                              }}>
                                {v.charAt(0).toUpperCase() + v.slice(1)}
                              </div>
                            </label>
                          ))}
                        </div>
                      )}

                      {field.type === 'dropdown' && (
                        <select
                          className="input-field"
                          value={answers[field.key] || ''}
                          onChange={e => setAnswer(field.key, e.target.value)}
                          style={{ appearance: 'none', cursor: 'pointer', width: '180px', flexShrink: 0 }}
                        >
                          <option value="">Select...</option>
                          {(field.values || []).map(v => (
                            <option key={v.key} value={v.key}>{v.title}</option>
                          ))}
                        </select>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>

        {/* Footer */}
        <div style={{ padding: '12px 20px', borderTop: '1px solid #DDE7F1', display: 'flex', justifyContent: 'flex-end' }}>
          <button className="btn-primary" style={{ padding: '0 24px', height: '40px', fontSize: '14px' }} onClick={onClose}>
            Save &amp; Close
          </button>
        </div>
      </div>
    </div>
  );
}

const HEALTH_CLASSES = [
  { value: 'PP', label: 'Preferred Plus' }, { value: 'P', label: 'Preferred' },
  { value: 'RP', label: 'Regular Plus' }, { value: 'R', label: 'Regular' },
  ...Array.from({ length: 16 }, (_, i) => ({ value: `T${i + 1}`, label: `Table ${i + 1}` })),
];

const NICOTINE_OPTIONS = [
  { value: '-1', label: 'Never used' }, { value: '0', label: 'Current user' },
  { value: '1', label: 'Last used 1 year ago' }, { value: '2', label: 'Last used 2 years ago' },
  { value: '3', label: 'Last used 3 years ago' }, { value: '4', label: 'Last used 4 years ago' },
  { value: '5', label: 'Last used 5+ years ago' },
];

function GenderButton({ value, selected, onChange }) {
  return (
    <label style={{ flex: 1, cursor: 'pointer' }}>
      <input type="radio" name="gender_code" value={value} checked={selected} onChange={() => onChange(value)} style={{ display: 'none' }} />
      <div style={{
        display: 'flex', justifyContent: 'center', alignItems: 'center',
        border: `1px solid ${selected ? '#2D87F0' : '#DDE7F1'}`,
        padding: '12px', borderRadius: '8px', cursor: 'pointer',
        background: selected ? '#2D87F0' : 'white',
        color: selected ? 'white' : '#334155',
        fontWeight: selected ? '600' : '400',
        transition: 'all 150ms',
      }}>
        {value === 'M' ? 'Male' : 'Female'}
      </div>
    </label>
  );
}

function SelectField({ label, value, onChange, options, show = true }) {
  if (!show) return null;
  return (
    <div>
      <div style={{ fontWeight: '600', marginBottom: '10px', fontSize: '14px' }}>{label}</div>
      <select
        value={value}
        onChange={e => onChange(e.target.value)}
        className="input-field"
        style={{ appearance: 'none', cursor: 'pointer' }}
      >
        {options.map(opt => (
          <option key={opt.value} value={opt.value}>{opt.label || opt.title}</option>
        ))}
      </select>
    </div>
  );
}

function QuoteResultRow({ row, companies, products, features, paymentMode, setPaymentMode, isSelected, onToggleSelect }) {
  const company = companies?.[row.company_id] || {};
  const product = products?.[row.product_id] || {};

  const monthlyParts = row.monthly_premium?.split('.') || ['--', '00'];
  const annualParts = row.annual_premium?.split('.') || ['--', '00'];

  return (
    <div style={{
      display: 'grid',
      gridTemplateColumns: '1fr 1fr 1fr 60px 60px',
      borderBottom: '1px solid #DDE7F1',
      opacity: row.hidden_reason ? 0.5 : 1,
      transition: 'opacity 150ms',
    }}
      onMouseEnter={e => row.hidden_reason && (e.currentTarget.style.opacity = '1')}
      onMouseLeave={e => row.hidden_reason && (e.currentTarget.style.opacity = '0.5')}
    >
      {/* Price */}
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '16px', background: 'white', gap: '8px' }}>
        {row.feature_codes?.includes('NE') && (
          <span style={{ fontSize: '11px', fontWeight: '600', border: '1px solid #2D87F0', color: '#2D87F0', borderRadius: '4px', padding: '1px 6px', textTransform: 'uppercase' }}>No Exam</span>
        )}
        {paymentMode === 'M' ? (
          <div style={{ whiteSpace: 'nowrap' }}>
            <span style={{ fontSize: '14px', fontWeight: '600', verticalAlign: 'top' }}>$</span>
            <span style={{ fontSize: '40px', fontWeight: '800', lineHeight: '0.8', verticalAlign: 'baseline' }}>{monthlyParts[0]}</span>
            <span style={{ fontSize: '18px', fontWeight: '700', verticalAlign: 'baseline' }}>.{monthlyParts[1]}</span>
          </div>
        ) : (
          <div style={{ whiteSpace: 'nowrap' }}>
            <span style={{ fontSize: '14px', fontWeight: '600', verticalAlign: 'top' }}>$</span>
            <span style={{ fontSize: '40px', fontWeight: '800', lineHeight: '0.8', verticalAlign: 'baseline' }}>{annualParts[0]}</span>
            <span style={{ fontSize: '18px', fontWeight: '700', verticalAlign: 'baseline' }}>.{annualParts[1]}</span>
          </div>
        )}
        {/* Monthly/Annual toggle */}
        <div style={{ position: 'relative', display: 'flex', background: '#E5E7EB', borderRadius: '999px', fontSize: '12px', fontWeight: '600', overflow: 'hidden', border: '1px solid #D1D5DB' }}>
          <div style={{
            position: 'absolute', top: 0, left: 0, height: '100%', width: '50%',
            background: '#2D87F0', borderRadius: '999px',
            transform: paymentMode === 'A' ? 'translateX(100%)' : 'translateX(0)',
            transition: 'transform 200ms',
          }}></div>
          <button onClick={() => setPaymentMode('M')} style={{ width: '52px', padding: '4px', textAlign: 'center', zIndex: 1, color: paymentMode === 'M' ? 'white' : '#334155', border: 'none', background: 'none', cursor: 'pointer', fontSize: '11px', fontWeight: '600' }}>Monthly</button>
          <button onClick={() => setPaymentMode('A')} style={{ width: '52px', padding: '4px', textAlign: 'center', zIndex: 1, color: paymentMode === 'A' ? 'white' : '#334155', border: 'none', background: 'none', cursor: 'pointer', fontSize: '11px', fontWeight: '600' }}>Annual</button>
        </div>
      </div>

      {/* Company */}
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '16px', background: 'white', cursor: 'pointer', gap: '4px' }}>
        <div style={{ width: '120px', height: '50px', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#F8FAFC', borderRadius: '6px', padding: '8px' }}>
          <div style={{ fontWeight: '700', fontSize: '13px', textAlign: 'center', color: '#334155' }}>{company.name}</div>
        </div>
        <span style={{ fontSize: '12px', color: '#2D87F0' }}>More Details</span>
      </div>

      {/* Product & Features */}
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '16px', background: 'white' }}>
        <div style={{ fontWeight: '600', fontSize: '13px', textAlign: 'center', color: row.hidden_reason ? '#DC2626' : '#334155', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: '180px', whiteSpace: 'nowrap' }}>
          {product.name}
          {row.hidden_reason && <span style={{ color: '#DC2626', fontSize: '18px' }}>*</span>}
        </div>
        <div style={{ display: 'flex', gap: '4px', marginTop: '8px', flexWrap: 'wrap', justifyContent: 'center' }}>
          {(row.feature_codes || []).map(code => (
            <span key={code} title={features?.[code]?.description_short} style={{
              width: '28px', height: '28px', borderRadius: '50%',
              border: '1px solid #2D87F0', display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: '11px', color: '#2D87F0', fontWeight: '600', cursor: 'pointer',
            }}>
              {code}
            </span>
          ))}
        </div>
      </div>

      {/* App link */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '16px', background: 'white' }}>
        <span style={{ fontSize: '12px', color: '#94A3B8', border: '1px solid #DDE7F1', borderRadius: '4px', padding: '6px 8px', cursor: 'not-allowed' }}>App</span>
      </div>

      {/* Select checkbox */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '16px', background: 'white', borderRight: '1px solid #DDE7F1', borderRadius: '0 4px 4px 0' }}>
        <label style={{ cursor: 'pointer', display: 'flex', alignItems: 'center' }}>
          <div style={{
            width: '20px', height: '20px', borderRadius: '4px',
            border: `2px solid ${isSelected ? '#2D87F0' : '#DDE7F1'}`,
            background: isSelected ? '#2D87F0' : 'white',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            cursor: 'pointer', transition: 'all 150ms',
          }} onClick={() => onToggleSelect(row.key)}>
            {isSelected && <i className="fas fa-check text-white" style={{ fontSize: '11px' }}></i>}
          </div>
        </label>
      </div>
    </div>
  );
}

export default function QuotePage() {
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);

  // Form state
  const [details, setDetails] = useState({
    engine_id: '1', gender_code: 'M',
    dob_month: '01', dob_day: '01', dob_year: '1985',
    height_ft: '5', height_in: '10', weight: '175',
    state_code: 'TX', nicotine: '-1', health_class_code: 'PP',
    coverage_amount: '100000', category_code: '3',
  });
  const [coverageAmount, setCoverageAmount] = useState('100000');
  const [showCustomCoverage, setShowCustomCoverage] = useState(false);
  const [customCoverage, setCustomCoverage] = useState('');

  // Underwriting state
  const [uwItems, setUwItems] = useState([]);
  const [uwForm, setUwForm] = useState(null);
  const [uwModalVisible, setUwModalVisible] = useState(false);
  const [uwAnswers, setUwAnswers] = useState({});

  // UI state
  const [activeView, setActiveView] = useState('start'); // 'start' | 'results' | 'review'
  const [quoteInfoOpen, setQuoteInfoOpen] = useState(true);
  const [showErrors, setShowErrors] = useState(false);
  const [errors, setErrors] = useState({});

  // Results state
  const [resultsData, setResultsData] = useState(null);
  const [loadingResults, setLoadingResults] = useState(false);
  const [paymentMode, setPaymentMode] = useState('M');
  const [selectedRows, setSelectedRows] = useState([]);
  const [filtersOpen, setFiltersOpen] = useState(false);

  // Review state
  const [customer, setCustomer] = useState({ name: '', email: '', phone: '' });
  const [customerLink, setCustomerLink] = useState('');
  const [savingState, setSavingState] = useState(0);

  // Required fields by engine
  const getRequiredFields = (engineId) => {
    const id = parseInt(engineId);
    // Term Life (1), No Medical Exam (2), Return of Premium (5), Living Benefits (10), Instant Issue (12), Agentless (14)
    if ([1, 2, 5, 10, 12, 14].includes(id)) return ['gender', 'coverage', 'term', 'state', 'nicotine', 'date_of_birth', 'height_weight', 'health'];
    // Final Expense (9), Whole Life (17)
    if ([9, 17].includes(id)) return ['gender', 'state', 'date_of_birth', 'nicotine', 'coverage'];
    // Accidental Death (6), Guaranteed Issue (7), Children's WL (13)
    if ([6, 7, 13].includes(id)) return ['gender', 'state', 'date_of_birth', 'coverage'];
    // Universal Life (4), Custom (18)
    if ([4, 18].includes(id)) return ['gender', 'state', 'date_of_birth', 'height_weight', 'nicotine', 'coverage', 'health'];
    // Default: full term fields
    return ['gender', 'coverage', 'term', 'state', 'nicotine', 'date_of_birth', 'height_weight', 'health'];
  };

  const requiredFields = getRequiredFields(details.engine_id);

  useEffect(() => {
    async function fetchConfig() {
      try {
        const [configRes, uwRes] = await Promise.all([
          api.get('/quoter/config'),
          api.get('/quoter/uw-form'),
        ]);
        setConfig(configRes.data);
        setUwForm(uwRes.data.form);
        // Set default coverage for engine
        const defaultCov = configRes.data.coverage_amounts?.['1']?.find(c => c.is_default);
        if (defaultCov) { setDetails(d => ({ ...d, coverage_amount: defaultCov.value })); setCoverageAmount(defaultCov.value); }
        const defaultTerm = configRes.data.coverage_types?.['1']?.find(t => t.is_default);
        if (defaultTerm) setDetails(d => ({ ...d, category_code: defaultTerm.value }));
      } catch (e) { console.error(e); }
      finally { setLoading(false); }
    }
    fetchConfig();
  }, []);

  // Update defaults when engine changes
  useEffect(() => {
    if (!config) return;
    const amounts = config.coverage_amounts?.[details.engine_id] || [];
    const types = config.coverage_types?.[details.engine_id] || [];
    const defaultAmt = amounts.find(a => a.is_default) || amounts[0];
    const defaultType = types.find(t => t.is_default) || types[0];
    if (defaultAmt) { setDetails(d => ({ ...d, coverage_amount: defaultAmt.value })); setCoverageAmount(defaultAmt.value); }
    if (defaultType) setDetails(d => ({ ...d, category_code: defaultType.value }));
  }, [details.engine_id]);

  const validateForm = () => {
    const errs = {};
    if (!details.dob_year || !details.dob_month || !details.dob_day) errs.date_of_birth = 'Please enter a valid date of birth.';
    if (!details.state_code) errs.state = 'Please select a state.';
    if (requiredFields.includes('height_weight') && (!details.height_ft || !details.weight)) errs.height_weight = 'Please enter height and weight.';
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const submitQuoteForm = async () => {
    setShowErrors(true);
    if (!validateForm()) return;

    setLoadingResults(true);
    setActiveView('results');
    setQuoteInfoOpen(false);
    setSelectedRows([]);

    try {
      const submitDetails = { ...details };
      if (!showCustomCoverage) {
        submitDetails.coverage_amount = coverageAmount;
      } else {
        submitDetails.coverage_amount = customCoverage.replace(/\D/g, '');
      }
      const res = await api.post('/quoter/run-quote', { details: submitDetails });
      setResultsData(res.data);
    } catch (e) {
      toast.error('Error running quote. Please try again.');
    } finally {
      setLoadingResults(false);
    }
  };

  const toggleRowSelect = (key) => {
    setSelectedRows(prev => prev.includes(key) ? prev.filter(k => k !== key) : [...prev, key]);
  };

  const getSelectedRowObjects = () => {
    if (!resultsData?.rows) return [];
    return selectedRows.map(k => resultsData.rows[k]).filter(Boolean);
  };

  const getSummaryString = () => {
    const coverageLabel = config?.coverage_amounts?.[details.engine_id]?.find(c => c.value === coverageAmount)?.title || `$${parseInt(coverageAmount || 0).toLocaleString()}`;
    const termLabel = config?.coverage_types?.[details.engine_id]?.find(t => t.value === details.category_code)?.title || '';
    const age = details.dob_year ? new Date().getFullYear() - parseInt(details.dob_year) : '';
    return [coverageLabel, termLabel, age ? `Age ${age}` : '', details.state_code].filter(Boolean).join(', ');
  };

  const handleSaveLead = async () => {
    if (savingState !== 0) return;
    setSavingState(1);
    try {
      await api.post('/quoter/save-lead', {
        customer,
        details,
        selected_rows: selectedRows,
      });
      setSavingState(2);
      toast.success('Lead saved successfully!');
      setTimeout(() => setSavingState(0), 3000);
    } catch (e) {
      setSavingState(0);
      toast.error('Error saving lead.');
    }
  };

  const handleGetCustomerLink = () => {
    const subdomain = config?.brand?.subdomain_alias || 'demo';
    const engineSlug = { '1': 'term-life', '2': 'final-expense', '3': 'whole-life', '4': 'universal-life' }[details.engine_id] || 'term-life';
    const params = new URLSearchParams();
    if (details.gender_code) params.set('gender', details.gender_code === 'M' ? 'Male' : 'Female');
    if (details.state_code) params.set('state', details.state_code);
    if (details.dob_year) params.set('dob', `${details.dob_year}-${details.dob_month?.padStart(2,'0')}-${details.dob_day?.padStart(2,'0')}`);
    if (details.coverage_amount) params.set('coverage_amount', details.coverage_amount);
    setCustomerLink(`https://${subdomain}.quoteplicity.com/quote/${engineSlug}?${params.toString()}`);
  };

  if (loading) {
    return (
      <ProtectedLayout>
        <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '24px 16px' }}>
          <div className="skeleton" style={{ height: '400px', borderRadius: '12px' }}></div>
        </div>
      </ProtectedLayout>
    );
  }

  const engines = config?.engines || {};
  const coverageAmountsByEngine = config?.coverage_amounts || {};
  const coverageTypesByEngine = config?.coverage_types || {};
  const states = config?.states || {};
  const productFeatures = config?.product_features || {};

  const coverageAmountOptions = (coverageAmountsByEngine[details.engine_id] || []).map(c => ({ value: c.value, label: c.title }));
  const coverageTypeOptions = (coverageTypesByEngine[details.engine_id] || []).map(t => ({ value: t.value, label: t.title }));
  const stateOptions = Object.entries(states).map(([k, v]) => ({ value: k, label: v }));

  const visibleSections = resultsData?.sections || [];

  return (
    <ProtectedLayout>
      <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '24px 16px', paddingBottom: selectedRows.length > 0 && activeView === 'results' ? '100px' : '24px' }}>

        {/* ===== START / RESULTS FORM ===== */}
        {(activeView === 'start' || activeView === 'results') && (
          <div style={{ marginBottom: '24px' }}>
            <div style={{ fontWeight: '700', fontSize: '20px', marginBottom: '20px', color: '#0F172A' }}>Run a Quote</div>

            <div className="portal-card" style={{ padding: '20px 24px' }}>
              {/* Collapsible header */}
              <div
                style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', cursor: 'pointer' }}
                onClick={() => setQuoteInfoOpen(!quoteInfoOpen)}
              >
                <div>
                  <span style={{ fontSize: '17px', fontWeight: '600' }}>Quote Information</span>
                  {!quoteInfoOpen && <span style={{ fontSize: '14px', color: '#64748B', marginLeft: '8px' }}>{getSummaryString()}</span>}
                </div>
                <i className={`fas fa-chevron-${quoteInfoOpen ? 'up' : 'down'} text-slate-400`}></i>
              </div>

              {quoteInfoOpen && (
                <>
                  <hr style={{ margin: '20px 0', borderColor: '#DDE7F1' }} />

                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '32px' }}>
                    {/* Left: Quote Details */}
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
                      <div style={{ fontWeight: '700', fontSize: '16px' }}>Quote details</div>

                      <SelectField
                        label="Engine"
                        value={details.engine_id}
                        onChange={v => setDetails(d => ({ ...d, engine_id: v }))}
                        options={Object.values(engines).map(e => ({ value: e.id, label: e.title }))}
                      />

                      <div>
                        <div style={{ fontWeight: '600', marginBottom: '10px', fontSize: '14px' }}>Coverage amount</div>
                        <select
                          value={showCustomCoverage ? 'custom' : coverageAmount}
                          onChange={e => {
                            if (e.target.value === 'custom') { setShowCustomCoverage(true); }
                            else { setShowCustomCoverage(false); setCoverageAmount(e.target.value); setDetails(d => ({ ...d, coverage_amount: e.target.value })); }
                          }}
                          className="input-field"
                          style={{ appearance: 'none', cursor: 'pointer' }}
                        >
                          {coverageAmountOptions.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                          <option value="custom">Custom{showCustomCoverage && customCoverage ? ` ($${parseInt(customCoverage.replace(/\D/g,'') || 0).toLocaleString()})` : ''}</option>
                        </select>
                        {showCustomCoverage && (
                          <input
                            type="text"
                            className="input-field"
                            style={{ marginTop: '8px' }}
                            placeholder="Enter coverage amount"
                            value={customCoverage}
                            onChange={e => {
                              const raw = e.target.value.replace(/\D/g, '');
                              setCustomCoverage(raw ? '$' + parseInt(raw).toLocaleString() : '');
                              setDetails(d => ({ ...d, coverage_amount: raw }));
                            }}
                          />
                        )}
                      </div>

                      {requiredFields.includes('term') && coverageTypeOptions.length > 1 && (
                        <SelectField
                          label="Term"
                          value={details.category_code}
                          onChange={v => setDetails(d => ({ ...d, category_code: v }))}
                          options={coverageTypeOptions}
                        />
                      )}

                      {/* show_underwriting = true: medication/condition search */}
                      <UnderwritingSearch uwItems={uwItems} setUwItems={setUwItems} />
                    </div>

                    {/* Right: Personal Details */}
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
                      <div style={{ fontWeight: '700', fontSize: '16px' }}>Personal details</div>

                      <div>
                        <div style={{ fontWeight: '600', marginBottom: '10px', fontSize: '14px' }}>Gender</div>
                        <div style={{ display: 'flex', gap: '10px' }}>
                          <GenderButton value="M" selected={details.gender_code === 'M'} onChange={v => setDetails(d => ({ ...d, gender_code: v }))} />
                          <GenderButton value="F" selected={details.gender_code === 'F'} onChange={v => setDetails(d => ({ ...d, gender_code: v }))} />
                        </div>
                      </div>

                      <div>
                        <div style={{ fontWeight: '600', marginBottom: '10px', fontSize: '14px' }}>Date of birth</div>
                        <div style={{ display: 'flex', gap: '8px' }}>
                          <input className="input-field" type="tel" placeholder="MM" value={details.dob_month} onChange={e => setDetails(d => ({ ...d, dob_month: e.target.value }))} style={{ flex: 1 }} maxLength={2} />
                          <input className="input-field" type="tel" placeholder="DD" value={details.dob_day} onChange={e => setDetails(d => ({ ...d, dob_day: e.target.value }))} style={{ flex: 1 }} maxLength={2} />
                          <input className="input-field" type="tel" placeholder="YYYY" value={details.dob_year} onChange={e => setDetails(d => ({ ...d, dob_year: e.target.value }))} style={{ flex: 2 }} maxLength={4} />
                        </div>
                        {showErrors && errors.date_of_birth && <div style={{ color: '#DC2626', fontSize: '13px', marginTop: '4px', fontWeight: '600' }}>{errors.date_of_birth}</div>}
                      </div>

                      {requiredFields.includes('height_weight') && (
                        <div>
                          <div style={{ fontWeight: '600', marginBottom: '10px', fontSize: '14px' }}>Build</div>
                          <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                            <span style={{ fontSize: '13px', whiteSpace: 'nowrap', color: '#64748B' }}>Height</span>
                            <input className="input-field" type="tel" placeholder="ft" value={details.height_ft} onChange={e => setDetails(d => ({ ...d, height_ft: e.target.value }))} style={{ flex: 1 }} maxLength={1} />
                            <input className="input-field" type="tel" placeholder="in" value={details.height_in} onChange={e => setDetails(d => ({ ...d, height_in: e.target.value }))} style={{ flex: 1 }} maxLength={2} />
                            <span style={{ fontSize: '13px', whiteSpace: 'nowrap', color: '#64748B' }}>Weight</span>
                            <input className="input-field" type="tel" placeholder="lbs" value={details.weight} onChange={e => setDetails(d => ({ ...d, weight: e.target.value }))} style={{ flex: 1 }} maxLength={3} />
                          </div>
                          {showErrors && errors.height_weight && <div style={{ color: '#DC2626', fontSize: '13px', marginTop: '4px', fontWeight: '600' }}>{errors.height_weight}</div>}
                        </div>
                      )}

                      <div>
                        <div style={{ fontWeight: '600', marginBottom: '10px', fontSize: '14px' }}>State</div>
                        <select className="input-field" value={details.state_code} onChange={e => setDetails(d => ({ ...d, state_code: e.target.value }))} style={{ appearance: 'none', cursor: 'pointer' }}>
                          <option value="">Select a state</option>
                          {stateOptions.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
                        </select>
                        {showErrors && errors.state && <div style={{ color: '#DC2626', fontSize: '13px', marginTop: '4px', fontWeight: '600' }}>{errors.state}</div>}
                      </div>

                      {requiredFields.includes('nicotine') && (
                        <SelectField
                          label="Nicotine use"
                          value={details.nicotine}
                          onChange={v => setDetails(d => ({ ...d, nicotine: v }))}
                          options={NICOTINE_OPTIONS}
                        />
                      )}

                      {requiredFields.includes('health') && (
                        <SelectField
                          label="Health class"
                          value={details.health_class_code}
                          onChange={v => setDetails(d => ({ ...d, health_class_code: v }))}
                          options={HEALTH_CLASSES.map(h => ({ value: h.value, title: h.label }))}
                        />
                      )}
                    </div>
                  </div>

                  <hr style={{ margin: '20px 0', borderColor: '#DDE7F1' }} />
                  {/* show_underwriting = true: two buttons */}
                  <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', alignItems: 'center', gap: '12px' }}>
                    <button
                      onClick={() => setUwModalVisible(true)}
                      className="btn-secondary"
                      style={{ padding: '14px 24px', fontSize: '15px', borderRadius: '8px', minWidth: '240px' }}
                    >
                      <i className="fas fa-edit mr-2"></i>Medical/Personal History
                    </button>
                    <button
                      onClick={submitQuoteForm}
                      data-testid="run-quote-button"
                      className="btn-primary"
                      style={{ padding: '14px 24px', fontSize: '15px', borderRadius: '8px', minWidth: '240px' }}
                    >
                      Run Quote
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        )}

        {/* ===== RESULTS VIEW ===== */}
        {activeView === 'results' && (
          <div style={{ marginTop: '24px' }}>
            {/* Quick edit controls */}
            {!loadingResults && resultsData && (
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: '16px', marginBottom: '20px' }}>
                <div>
                  <div style={{ marginBottom: '8px', fontSize: '13px', color: '#64748B' }}>Coverage:</div>
                  <select className="input-field" value={showCustomCoverage ? 'custom' : coverageAmount} onChange={e => { if (e.target.value === 'custom') { setShowCustomCoverage(true); } else { setShowCustomCoverage(false); setCoverageAmount(e.target.value); setDetails(d => ({ ...d, coverage_amount: e.target.value })); submitQuoteForm(); } }} style={{ appearance: 'none', cursor: 'pointer' }}>
                    {coverageAmountOptions.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                    <option value="custom">Custom</option>
                  </select>
                </div>
                {requiredFields.includes('term') && coverageTypeOptions.length > 1 && (
                  <div>
                    <div style={{ marginBottom: '8px', fontSize: '13px', color: '#64748B' }}>Term:</div>
                    <select className="input-field" value={details.category_code} onChange={e => { setDetails(d => ({ ...d, category_code: e.target.value })); setTimeout(submitQuoteForm, 50); }} style={{ appearance: 'none', cursor: 'pointer' }}>
                      {coverageTypeOptions.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                    </select>
                  </div>
                )}
                <div>
                  <div style={{ marginBottom: '8px', fontSize: '13px', color: '#64748B' }}>Payment type:</div>
                  <select className="input-field" value={paymentMode} onChange={e => setPaymentMode(e.target.value)} style={{ appearance: 'none', cursor: 'pointer' }}>
                    <option value="M">Monthly</option>
                    <option value="A">Annual</option>
                  </select>
                </div>
                <div>
                  <div style={{ marginBottom: '8px', fontSize: '13px', color: '#64748B' }}>&nbsp;</div>
                  <button
                    onClick={() => setFiltersOpen(!filtersOpen)}
                    style={{
                      width: '100%', height: '40px', borderRadius: '8px',
                      border: `1px solid ${filtersOpen ? '#2D87F0' : '#2D87F0'}`,
                      background: filtersOpen ? '#2D87F0' : 'white',
                      color: filtersOpen ? 'white' : '#2D87F0',
                      fontWeight: '600', fontSize: '14px', cursor: 'pointer',
                    }}
                  >
                    Filter Results
                  </button>
                </div>
              </div>
            )}

            {/* Loading skeleton */}
            {loadingResults && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                {[1, 2, 3, 4].map(i => (
                  <div key={i} className="skeleton" style={{ height: '100px', borderRadius: '8px' }}></div>
                ))}
              </div>
            )}

            {/* Results table */}
            {!loadingResults && resultsData && (
              <>
                {visibleSections.length === 0 ? (
                  <div className="portal-card" style={{ padding: '32px', textAlign: 'center', fontWeight: '600', fontSize: '16px' }}>
                    Your search returned no results.
                  </div>
                ) : (
                  <div className="portal-card" style={{ overflow: 'hidden' }}>
                    {/* Table header */}
                    <div style={{
                      display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 60px 60px',
                      background: '#F8FAFC', borderBottom: '1px solid #DDE7F1',
                    }}>
                      {['Price', 'Company', 'Product & Features', 'Actions', 'Select'].map(h => (
                        <div key={h} style={{ padding: '12px 16px', fontWeight: '600', fontSize: '13px', textAlign: 'center', borderLeft: '1px solid #DDE7F1', borderRight: h === 'Select' ? '1px solid #DDE7F1' : 'none' }}>
                          {h}
                        </div>
                      ))}
                    </div>

                    {visibleSections.map((section, si) => (
                      <div key={section.key}>
                        {section.title && (
                          <div style={{ textAlign: 'center', fontSize: '24px', fontWeight: '800', padding: '16px', borderBottom: '1px solid #DDE7F1', marginTop: si > 0 ? '16px' : 0 }}>
                            {section.title}
                          </div>
                        )}
                        {(section.rows || []).map(rowKey => {
                          const row = resultsData.rows?.[rowKey];
                          if (!row) return null;
                          return (
                            <QuoteResultRow
                              key={rowKey}
                              row={row}
                              companies={resultsData.companies}
                              products={resultsData.products}
                              features={productFeatures}
                              paymentMode={paymentMode}
                              setPaymentMode={setPaymentMode}
                              isSelected={selectedRows.includes(rowKey)}
                              onToggleSelect={toggleRowSelect}
                            />
                          );
                        })}
                      </div>
                    ))}
                  </div>
                )}
              </>
            )}
          </div>
        )}

        {/* ===== REVIEW SELECTED ===== */}
        {activeView === 'review' && (
          <div style={{ marginTop: '24px' }}>
            <div style={{ display: 'flex', alignItems: 'center', marginBottom: '28px' }}>
              <button
                onClick={() => setActiveView('results')}
                style={{ width: '32px', height: '32px', display: 'flex', alignItems: 'center', justifyContent: 'center', border: '1px solid #334155', borderRadius: '6px', background: 'white', cursor: 'pointer' }}
              >
                <i className="fas fa-arrow-left"></i>
              </button>
              <div style={{ marginLeft: '14px', fontWeight: '600', fontSize: '20px' }}>Review Selected Policies</div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: '32px' }}>
              {/* Selected rows */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                {getSelectedRowObjects().map(row => {
                  const company = resultsData?.companies?.[row.company_id] || {};
                  const product = resultsData?.products?.[row.product_id] || {};
                  const monthlyParts = row.monthly_premium?.split('.') || ['--', '00'];
                  return (
                    <div key={row.key} className="portal-card" style={{ display: 'flex', alignItems: 'center', gap: '20px', padding: '20px 24px' }}>
                      <div style={{ whiteSpace: 'nowrap' }}>
                        <span style={{ fontSize: '12px', verticalAlign: 'top' }}>$</span>
                        <span style={{ fontSize: '36px', fontWeight: '800', verticalAlign: 'baseline' }}>{monthlyParts[0]}</span>
                        <span style={{ fontSize: '18px', fontWeight: '700', verticalAlign: 'baseline' }}>.{monthlyParts[1]}</span>
                        <span style={{ fontSize: '12px', verticalAlign: 'baseline' }}>/mo</span>
                      </div>
                      <div style={{ width: '120px', height: '50px', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#F8FAFC', borderRadius: '6px', padding: '8px' }}>
                        <div style={{ fontWeight: '700', fontSize: '12px', textAlign: 'center' }}>{company.name}</div>
                      </div>
                      <div style={{ flex: 1, fontWeight: '600', fontSize: '14px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{product.name}</div>
                      <div style={{ color: '#2D87F0', fontWeight: '600', fontSize: '14px' }}>Selected</div>
                    </div>
                  );
                })}
              </div>

              {/* Save & Share panel */}
              <div className="portal-card" style={{ padding: '24px', height: 'fit-content' }}>
                <div style={{ fontWeight: '700', fontSize: '16px', marginBottom: '20px' }}>Save &amp; Share</div>
                <hr style={{ borderColor: '#DDE7F1', marginBottom: '20px' }} />

                <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                  <div>
                    <div style={{ fontWeight: '600', fontSize: '13px', marginBottom: '6px' }}>Customer Name</div>
                    <input className="input-field" type="text" placeholder="Customer Name" value={customer.name} onChange={e => setCustomer(c => ({ ...c, name: e.target.value }))} />
                  </div>
                  <div>
                    <div style={{ fontWeight: '600', fontSize: '13px', marginBottom: '6px' }}>Email</div>
                    <input className="input-field" type="email" placeholder="Customer Email" value={customer.email} onChange={e => setCustomer(c => ({ ...c, email: e.target.value }))} />
                  </div>
                  <div>
                    <div style={{ fontWeight: '600', fontSize: '13px', marginBottom: '6px' }}>Phone</div>
                    <input className="input-field" type="tel" placeholder="(xxx) xxx-xxxx" value={customer.phone} onChange={e => setCustomer(c => ({ ...c, phone: e.target.value }))} />
                  </div>

                  <hr style={{ borderColor: '#DDE7F1' }} />

                  {!customerLink ? (
                    <button
                      onClick={handleGetCustomerLink}
                      className="btn-secondary"
                      style={{ width: '100%', height: '48px', fontSize: '14px' }}
                    >
                      Get Customer Link
                    </button>
                  ) : (
                    <div>
                      <a href={customerLink} target="_blank" rel="noreferrer" style={{ fontSize: '12px', color: '#2D87F0', wordBreak: 'break-all' }}>{customerLink}</a>
                    </div>
                  )}

                  <hr style={{ borderColor: '#DDE7F1' }} />

                  <button
                    onClick={handleSaveLead}
                    disabled={savingState !== 0}
                    className="btn-primary"
                    style={{ width: '100%', height: '48px', fontSize: '14px', opacity: savingState !== 0 ? 0.7 : 1 }}
                  >
                    {['Save as Lead', 'Saving...', 'Saved!'][savingState]}
                  </button>

                  <button
                    onClick={() => { setActiveView('start'); setQuoteInfoOpen(true); setSelectedRows([]); setResultsData(null); }}
                    className="btn-secondary"
                    style={{ width: '100%', height: '48px', fontSize: '14px' }}
                  >
                    Start Over
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* ===== STICKY CONTINUE BAR ===== */}
      {selectedRows.length > 0 && activeView === 'results' && (
        <div style={{
          position: 'fixed', bottom: 0, left: 0, right: 0, height: '72px',
          background: 'white', borderTop: '1px solid #DDE7F1',
          boxShadow: '0 -4px 20px rgba(0,0,0,0.08)',
          display: 'flex', alignItems: 'center', justifyContent: 'flex-end',
          padding: '0 24px', gap: '16px', zIndex: 49,
        }} data-testid="sticky-continue-bar">
          <div style={{ flex: 1, display: 'flex', justifyContent: 'flex-end', gap: '12px', alignItems: 'center', overflow: 'hidden', paddingRight: '160px' }}>
            {getSelectedRowObjects().map(row => {
              const company = resultsData?.companies?.[row.company_id] || {};
              return (
                <div key={row.key} style={{ height: '40px', padding: '4px 12px', background: '#F8FAFC', borderRadius: '6px', display: 'flex', alignItems: 'center', fontWeight: '600', fontSize: '12px', border: '1px solid #DDE7F1', whiteSpace: 'nowrap' }}>
                  {company.name}
                </div>
              );
            })}
          </div>
          <button
            onClick={() => setActiveView('review')}
            className="btn-primary"
            style={{ padding: '0 32px', height: '44px', fontSize: '15px', whiteSpace: 'nowrap' }}
            data-testid="continue-button"
          >
            Continue ({selectedRows.length})
          </button>
          <button
            onClick={() => setSelectedRows([])}
            style={{ background: 'none', border: 'none', color: '#2D87F0', cursor: 'pointer', fontSize: '14px', whiteSpace: 'nowrap', marginRight: '160px' }}
            data-testid="deselect-all-button"
          >
            Deselect All
          </button>
        </div>
      )}

      {/* UW History Modal */}
      <UwHistoryModal
        visible={uwModalVisible}
        onClose={() => setUwModalVisible(false)}
        form={uwForm}
        answers={uwAnswers}
        setAnswers={setUwAnswers}
      />
    </ProtectedLayout>
  );
}
